import moment from "moment";
import { ElMessageBox } from "element-plus";
import {
  deleteRequest,
  getRequest,
  postRequest,
  downloadWithSavePicker,
} from "~/apis/requests/useApiCall";
import { TableDataWithPagination } from "~/types/generics/tableDataWithPagination";
import { Pagination } from "~/types/pagination";
import { ProductSummary } from "~/types/productSummary";
import { Partner } from "~/projects/crm/type/partner";

export const usePartnerStore = definePiniaStore("usePartnerStore", {
  state: () => ({
    _loading: true,
    _tableData: [] as Partner[],
    _selectedPartnerForEdit: {} as Partner,
    _filterData: {},
    _simpleFilter: {},
    _allPartners: [] as Partner[],
    _simplePartners: [] as Partner[],
    _partner: {} as Partner,
    _pagination: {} as Pagination,
    showEditModal: false,
    showCreateModal: false,
  }),
  getters: {
    tableData: (state) => state._tableData,
    selectedPartnerForEdit: (state) => state._selectedPartnerForEdit,
    pagination: (state) => state._pagination,
    allPartner: (state) => state._allPartners,
    industryList: () => [],
    partner: (state) => state._partner,
    simplePartners: (state) => state._simplePartners,
  },
  actions: {
    async getPartners(pageNo = 1, sortBy = "id", ascending = false) {
      this._loading = true;
      const sortDir = ascending ? "asc" : "desc";
      let url = `crm/partners?pagination=true&page=${pageNo}&sort_column=${sortBy}&dir=${sortDir}`;
      for (const [key, value] of Object.entries(this.$state._filterData)) {
        if (!value && value !== 0) continue;
        url += `&${key}=${value}`;
      }

      const { response, error } = await getRequest<
        TableDataWithPagination<Partner>
      >(url);
      if (!error) {
        const { data, ...pagination } = response.data;
        this.$state._tableData = data;
        this.$state._pagination = pagination;
        this._loading = false;
      }
    },

    async exportTable() {
      let url = `crm/partners/export?`;
      for (const [key, value] of Object.entries(this._filterData)) {
        if (value === null) continue;
        url += `${key}=${value}&`;
      }

      const date = moment().format("YYYY-MM-DD");
      const fileName = `${"partner-table"}-${date}`;

      await downloadWithSavePicker(url, fileName);
    },

    async createPartner(partner: any) {
      const { response, error } = await postRequest<any>(
        "crm/partners",
        partner,
        true,

        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );
      if (error) {
        ElMessageBox({
          title: "Partner creation failed",
          message: error?.response?.data.message,
          type: "error",
        });
        return;
      }
      return response;
    },
    async updatePartner(id: number, partner: any) {
      const { response, error } = await postRequest<any>(
        `crm/partners/${id}`,
        partner,
        true,

        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );
      if (error) {
        const errors = error.response?.data?.errors || {};

        let formattedErrors = `<ul style="list-style-type: disc; padding-left: 20px;">`;

        Object.keys(errors).forEach((key) => {
          errors[key].forEach((message) => {
            formattedErrors += `<li>${message}</li>`;
          });
        });

        formattedErrors += "</ul>";
        ElMessageBox({
          title: "Partner update failed",
          message: formattedErrors || error.message,
          dangerouslyUseHTMLString: true,
          type: "info",
        });
        return;
      }
      return response;
    },

    async deletePartner(id: number) {
      return await deleteRequest(`crm/partners/${id}`);
    },

    async getAllPartners(category_id?: number) {
      const url = category_id
        ? `crm/partner?category_id=${category_id}`
        : `crm/partners`;
      const { response, error } = await getRequest<Partner[]>(url);
      if (!error) {
        this.$state._allPartners = response.data;
      }
    },

    async getSimplePartners() {
      let url = `crm/partners/simple?`;
      for (const [key, value] of Object.entries(this._simpleFilter)) {
        if (!value) continue;
        url += `&${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
      }
      const { response, error } = await getRequest<Partner[]>(url);
      if (!error) {
        this.$state._simplePartners = response.data;
      }
    },

    async getPartner(id: number) {
      const { response, error } = await getRequest<Partner>(
        `crm/partners/${id}`,
      );
      if (!error) {
        this.$state._partner = response.data;
      }
    },

    // mutations
    setCreateModalVisibility(val: boolean) {
      this.$state.showCreateModal = val;
    },
    setEditModalVisibility(val: boolean) {
      this.$state.showEditModal = val;
    },
    setEditData(partner: Partner) {
      this.$state._selectedPartnerForEdit = partner;
    },
    setFilterData(filter: any) {
      this.$state._filterData = filter;
    },
    setSimpleFilter(filter: any) {
      this.$state._simpleFilter = filter;
    },
  },
});
